import React, { useContext } from 'react';
import { IViewPortionContext, ViewPortionContext } from '../../viewPortionState/viewPortionContext';
import Text from 'components/Text/Text';
import style from './index.module.scss';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import { priceStrategies } from 'appConstants/dishes';
import { useTranslation } from 'react-i18next';
import { Badge } from 'components/Badge';
import { MenuTranslationForm } from 'components/TranslationForm/MenuTranslationForm';
import { ItemList } from 'components/ViewPage/ItemList';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import classNames from 'classnames';

export const PortionFullInfo: React.FC = () => {
	const {
		portionFullInfo,
		setPortionModifierGroupId,
		portionModifierGroupId,
		portionModifierGroupModifiers,
		setTranslationTab,
		selectedTranslationTab,
	} = useContext<IViewPortionContext>(ViewPortionContext);

	const { t } = useTranslation('form');
	const isDisabledValue = t('isDisabledLabel');
	const priceStrategy = priceStrategies.find((strategy) => strategy.id === portionFullInfo?.priceStrategy);
	const priceStrategyValue = priceStrategy ? t(priceStrategy.name) : '-';
	const innerFieldsClasses = classNames(style.fieldsWrapper, style.innerFieldsWrapper);

	return (
		<>
			<div className={style.headerContainer}>
				<div className={style.headerRow}>
					<Text
						className={style.headingText}
						translationKey={portionFullInfo?.name}
						size="xl"
						weight="bold"
					/>
					{portionFullInfo?.isCreatedFromPos ? (
						<Badge color="blue">
							<Text
								translationKey="POS"
								asFragment={true}
								data-test={`isCreatedOnPos=${portionFullInfo?.isCreatedFromPos}`}
							/>
						</Badge>
					) : (
						<Badge color="purple">
							<Text
								translationKey="AMS"
								asFragment={true}
								data-test={`isCreatedOnPos=${portionFullInfo?.isCreatedFromPos}`}
							/>
						</Badge>
					)}
				</div>
				<div className={style.headerRow}>
					<Text translationNameSpace="form" translationKey="dishLabel" theme="neutralLight" size="xs" />
					<Text translationNameSpace="form" translationKey={portionFullInfo?.dish.name} withTooltip={true} />
				</div>
			</div>
			<div className={style.container} data-test="PortionMainInfo">
				<Text
					translationNameSpace="form"
					translationKey="mainInfoHeading"
					theme="secondary"
					size="l"
					weight="bold"
					display="inline"
				/>
				<div className={style.fieldsWrapper}>
					<div className={style.row}>
						<FullInfoField label="portionNameLabel" value={portionFullInfo?.name} />
						<FullInfoField label="posNameLabel" value={portionFullInfo?.posName} />
					</div>
					<div className={style.row}>
						<FullInfoField label="articleNumberLabel" value={portionFullInfo?.articleNumber} />
						<FullInfoField label="customerLabel" value={portionFullInfo?.customer.name} />
					</div>
					<div className={style.row}>
						<FullInfoField label="restaurantLabel" value={portionFullInfo?.restaurant.name} />
						<FullInfoField label="dishLabel" value={portionFullInfo?.dish.name} />
					</div>
					<div className={style.row}>
						<FullInfoField
							label="signedPriceLabel"
							value={
								typeof portionFullInfo?.price === 'number'
									? `${portionFullInfo?.currencySymbol || ''} ${portionFullInfo?.price || 0}`
									: '-'
							}
						/>
						<FullInfoField label="externalIdLabel" value={portionFullInfo?.externalId} />
					</div>
					<div className={style.row}>
						<FullInfoField label="priceStrategyLabel" value={priceStrategyValue} />
					</div>
					<div className={style.row}>
						<FullInfoField
							label="isDisabledLabel"
							value={isDisabledValue}
							prefixIcon="asCheckbox"
							prefixIconName={portionFullInfo?.isDisabled ? 'checkMark' : 'crossMark'}
							prefixIconIsChecked={portionFullInfo?.isDisabled}
						/>
					</div>
				</div>
			</div>
			<div className={style.container} data-test="PortionTranslationInfo">
				<Text
					translationNameSpace="form"
					translationKey="translationsHeading"
					theme="secondary"
					size="l"
					weight="bold"
					display="inline"
				/>
				<div className={style.fieldsWrapper}>
					<MenuTranslationForm
						className={style.translationForm}
						setTranslationTab={setTranslationTab}
						isShowLanguageName={false}
						IDSelectedCustomer={portionFullInfo?.customer.id || ''}
						translations={portionFullInfo?.translations || []}
						selectedTranslationTab={selectedTranslationTab}
						nameLabel="portionNameLabel"
						hasDescriptionFullInfoField={false}
					/>
				</div>
			</div>
			<div className={style.container} data-test="PortionModGroupsInfo">
				<Text
					translationNameSpace="form"
					translationKey="modifierGroupsHeading"
					theme="secondary"
					size="l"
					weight="bold"
					display="inline"
				/>
				{portionFullInfo?.modifierGroups.length ? (
					<>
						<ItemList
							items={portionFullInfo.modifierGroups}
							pathToItem="/menu-v2/modifier-groups/view?modifierGroupID="
						/>
						<div className={innerFieldsClasses}>
							<Text
								translationNameSpace="form"
								translationKey="modifiersHeading"
								theme="secondary"
								size="m"
								weight="bold"
								display="inline"
							/>
							<OutsideFormSelect
								containerClassName={style.innerContainer}
								testId="portionModifierGroupModifiers"
								label="modifierGroupLabel"
								name="portionModifierGroupModifiers"
								value={portionModifierGroupId}
								options={portionFullInfo.modifierGroups}
								onChange={setPortionModifierGroupId}
							/>
							{!!portionModifierGroupModifiers.length && (
								<ItemList
									items={portionModifierGroupModifiers}
									pathToItem="/menu-v2/modifiers/view?modifierID="
								/>
							)}
						</div>
					</>
				) : (
					<>
						<Text size="s" translationNameSpace="form" translationKey="noPortionModGroupsHeading" />
						<FullInfoField containerClassName={style.marginTopZero} label="noPortionModGroupsDescription" />
					</>
				)}
			</div>
		</>
	);
};
